import { useState } from 'react';
import type { AdminUser } from '../types';
import { Icon, Avatar, Badge, TierBadge, Meter, fmt } from '../atoms';
import { BarChart } from '../charts';
import { costSeries, tokenSeries, ORG } from '../data';

const MONTH_LABELS = [0, 7, 14, 21, 29].map(i => ({ i, t: 'd' + (i + 1) }));

export function AIUsage({ users, onOpen }: { users: AdminUser[]; onOpen: (u: AdminUser) => void }) {
  const [mode, setMode] = useState<'cost' | 'tokens'>('cost');
  const active = users.filter(u => u.tokens > 0);
  const rows = [...active].sort((a, b) => mode === 'cost' ? b.cost - a.cost : b.tokens - a.tokens);
  const costMonth = costSeries.reduce((a, b) => a + b, 0);
  const tokMonth = tokenSeries.reduce((a, b) => a + b, 0) * 1000;
  const avg = active.length ? costMonth / ORG.totalUsers : 0;
  const projected = costMonth * 1.06;
  const series = mode === 'cost' ? costSeries : tokenSeries;

  const kpi = (label: string, value: string, sub: React.ReactNode) => (
    <div className="card kpi">
      <div className="k-h"><span className="ic"><Icon name="coin" size={14} /></span>{label}</div>
      <div className="k-v">{value}</div>
      <div className="k-sub">{sub}</div>
    </div>
  );

  return (
    <>
      <div className="kpis">
        {kpi('Spend · 30d', fmt.cost(costMonth), <span>{fmt.tokens(tokMonth)} tokens</span>)}
        {kpi('Avg / user', fmt.cost(avg), <span>across {ORG.totalUsers} users</span>)}
        {kpi('Projected month-end', fmt.cost(projected), <>
          <span style={{ color: 'var(--bad)', fontFamily: 'var(--label-font)', fontWeight: 700 }}>+6%</span>
          <span>run-rate</span>
        </>)}
        {kpi('Users at ≥90% cap', String(users.filter(u => fmt.pct(u.cost, u.capCost) >= 90).length),
          <span>AI paused when hit</span>)}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="panel-h">
          <h3>System AI usage</h3>
          <div className="grow" />
          <div className="seg">
            <button className={mode === 'cost' ? 'on' : ''} onClick={() => setMode('cost')}>£ cost</button>
            <button className={mode === 'tokens' ? 'on' : ''} onClick={() => setMode('tokens')}>Tokens</button>
          </div>
        </div>
        <div className="chartwrap">
          <BarChart data={series} xlabels={MONTH_LABELS} height={170}
            fmtY={mode === 'cost' ? v => '£' + Math.round(v) : v => fmt.tokens(v * 1000)} />
        </div>
      </div>

      <div className="sec-h">
        <h2>By user</h2>
        <span className="c">sorted by {mode === 'cost' ? 'cost' : 'tokens'}</span>
      </div>

      <div className="tablewrap">
        <table className="at">
          <thead>
            <tr><th>User</th><th>Tier</th><th>Tokens</th><th>Cost</th><th>Requests</th><th>Cap status</th></tr>
          </thead>
          <tbody>
            {rows.map(u => {
              const cp = fmt.pct(u.cost, u.capCost), tp = fmt.pct(u.tokens, u.capTokens);
              const worst = Math.max(cp, tp);
              return (
                <tr key={u.id} onClick={() => onOpen(u)}>
                  <td>
                    <div className="ucell">
                      <Avatar size={32} hue={u.hue} initials={u.initials} />
                      <div className="who"><div className="n">{u.name}</div><div className="e">{u.email}</div></div>
                    </div>
                  </td>
                  <td><TierBadge tier={u.tier} /></td>
                  <td>
                    <div style={{ width: 150 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span className="mono" style={{ fontWeight: 600 }}>{fmt.tokens(u.tokens)}</span>
                        <span className="mono" style={{ color: 'var(--faint)' }}>{tp}%</span>
                      </div>
                      <Meter pct={tp} />
                    </div>
                  </td>
                  <td className="mono" style={{ fontWeight: 600 }}>
                    {fmt.cost(u.cost)}
                    <span style={{ color: 'var(--faint)', fontWeight: 400 }}> / {fmt.cost(u.capCost)}</span>
                  </td>
                  <td className="mono">{fmt.int(u.reqs)}</td>
                  <td>
                    {worst >= 100 ? <Badge kind="bad">Cap hit</Badge>
                      : worst >= 90 ? <Badge kind="warn">Near cap</Badge>
                      : <Badge kind="ok">Healthy</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
